import { z } from 'zod';

import { actionIdSchema } from './chat-ids';

export const resumeActionDecisionSchema = z.object({
    accepted: z.boolean(),
    actionId: actionIdSchema,
    type: z.literal('decision'),
});

export type ResumeActionDecision = z.infer<typeof resumeActionDecisionSchema>;

export const resumeActionInputSchema = z.object({
    actionId: actionIdSchema,
    input: z.string(),
    type: z.literal('input'),
});

export type ResumeActionInput = z.infer<typeof resumeActionInputSchema>;

export const editedProposalSectionSchema = z.object({
    id: z.string(),
    value: z.string(),
});

export type EditedProposalSection = z.infer<typeof editedProposalSectionSchema>;

export const editedProposalFieldSchema = z.object({
    id: z.string(),
    value: z.string(),
});

export type EditedProposalField = z.infer<typeof editedProposalFieldSchema>;

// Only the fields the user changed on one proposed task; keys must exist in the proposal.
export const editedProposalTaskSchema = z.object({
    classification: z.object({ reasoning: z.string().nullish(), value: z.string() }).nullish(),
    description: z.string().nullish(),
    estimatedEffort: z
        .object({
            maximumHoursPerWeek: z.number(),
            minimumHoursPerWeek: z.number(),
            reasoning: z.string().nullish(),
        })
        .nullish(),
    key: z.string(),
    requiredSkills: z.array(z.string()).optional(),
    title: z.string().optional(),
    workflow: z
        .object({
            steps: z.array(
                z.object({
                    description: z.string().nullish(),
                    physicality: z.string().nullish(),
                    title: z.string(),
                }),
            ),
        })
        .nullish(),
});

export type EditedProposalTask = z.infer<typeof editedProposalTaskSchema>;

// Sent alongside the accept `input` action; carries only the fields the user changed.
// The client always sends `sections` (possibly empty) and sends `fields` only when a requested
// value row was edited. `description` mirrors the backend contract but is reserved: nothing in
// the client emits it (v1 has no description editing).
export const resumeActionEditedContentSchema = z.object({
    actionId: actionIdSchema,
    content: z.object({
        description: z.string().optional(),
        fields: z.array(editedProposalFieldSchema).optional(),
        sections: z.array(editedProposalSectionSchema).optional(),
        tasks: z.array(editedProposalTaskSchema).optional(),
        title: z.string().optional(),
    }),
    type: z.literal('edited-content'),
});

export type ResumeActionEditedContent = z.infer<typeof resumeActionEditedContentSchema>;

export const resumeActionSchema = z.discriminatedUnion('type', [
    resumeActionDecisionSchema,
    resumeActionInputSchema,
    resumeActionEditedContentSchema,
]);

export type ResumeAction = z.infer<typeof resumeActionSchema>;

/** The payload of a resume entry that answers a question; the interrupt's `responseSchema` describes it. */
export const chatResumePayloadSchema = z.object({
    actions: z.array(resumeActionSchema).readonly(),
});

export type ChatResumePayload = z.infer<typeof chatResumePayloadSchema>;
